import type { LayerRuleStypeElement, LayerRuleStyle } from 'models/LayerRuleStyle';
import type { SymbologyModel } from 'models/SymbologyModel';
import type { RefreshableGeoJsonLayer } from 'types/layerRefresh';

type GeometryKind = 'point' | 'polygon' | 'line';

export type GetLayerStyleFn = (
  geometryType: GeometryKind,
  key: string,
  category: string,
  styles: Record<string, unknown>,
) => Record<string, unknown>;

export type ComputeLayerStyleFn = (
  style: SymbologyModel,
  type: string,
) => Record<string, unknown>;

export interface ApplyGeoJsonLayerRendererParams {
  readonly layer: RefreshableGeoJsonLayer;
  readonly nodeKey: string;
  readonly nodeCategory: string;
  readonly layerRules: LayerRuleStyle | undefined;
  readonly layersStyle: Record<string, unknown>;
  readonly getStyle: GetLayerStyleFn;
  readonly computeStyle: ComputeLayerStyleFn;
  readonly clusterConfig: unknown;
  readonly clusteringEnabled: boolean;
}

function uniqueRuleValue(rule: LayerRuleStypeElement): string | undefined {
  return rule.dataType === 'Integer' ? rule.label : `${rule.name}-${rule.value}`;
}

function buildUniqueValueRenderer(
  geometryType: GeometryKind,
  nodeKey: string,
  nodeCategory: string,
  layerRules: LayerRuleStyle,
  layersStyle: Record<string, unknown>,
  getStyle: GetLayerStyleFn,
  computeStyle: ComputeLayerStyleFn,
): Record<string, unknown> {
  const uniqueRendered = layerRules.layerRules;
  const mapSymbol = (rule: LayerRuleStypeElement): Record<string, unknown> => {
    if (geometryType === 'polygon') {
      return { ...computeStyle(rule.style, 'simple-fill') };
    }
    return { ...rule.style };
  };

  return {
    type: 'unique-value',
    defaultSymbol: {
      ...getStyle(geometryType, nodeKey, nodeCategory, layersStyle),
    },
    valueExpression: layerRules.expression,
    uniqueValueInfos: uniqueRendered.map((rule) => ({
      value: uniqueRuleValue(rule),
      symbol: mapSymbol(rule),
    })),
  };
}

function buildSimpleRenderer(
  geometryType: GeometryKind,
  nodeKey: string,
  nodeCategory: string,
  layersStyle: Record<string, unknown>,
  getStyle: GetLayerStyleFn,
): Record<string, unknown> {
  return {
    type: 'simple',
    symbol: {
      ...getStyle(geometryType, nodeKey, nodeCategory, layersStyle),
    },
  };
}

function applyPointClustering(
  layer: RefreshableGeoJsonLayer,
  nodeKey: string,
  layersStyle: Record<string, unknown>,
  clusterConfig: unknown,
  clusteringEnabled: boolean,
): void {
  layer.featureReduction = clusteringEnabled ? clusterConfig : null;
  const featureReduction = layer.featureReduction as
    | { symbol?: unknown }
    | null
    | undefined;
  if (!featureReduction) {
    return;
  }
  const layerStyle = layersStyle[nodeKey] as { currentColor?: string } | undefined;
  featureReduction.symbol = {
    type: 'simple-marker',
    color: layerStyle?.currentColor ?? 'red',
  };
}

export function applyGeoJsonLayerRenderer(
  params: ApplyGeoJsonLayerRendererParams,
): void {
  const {
    layer,
    nodeKey,
    nodeCategory,
    layerRules,
    layersStyle,
    getStyle,
    computeStyle,
    clusterConfig,
    clusteringEnabled,
  } = params;

  const uniqueRendered = layerRules?.layerRules;
  const geometryType = layer.geometryType;

  if (geometryType === 'point') {
    layer.renderer = uniqueRendered
      ? buildUniqueValueRenderer(
          'point',
          nodeKey,
          nodeCategory,
          layerRules as LayerRuleStyle,
          layersStyle,
          getStyle,
          computeStyle,
        )
      : buildSimpleRenderer('point', nodeKey, nodeCategory, layersStyle, getStyle);
    applyPointClustering(
      layer,
      nodeKey,
      layersStyle,
      clusterConfig,
      clusteringEnabled,
    );
    return;
  }

  if (geometryType === 'polygon') {
    layer.renderer = uniqueRendered
      ? buildUniqueValueRenderer(
          'polygon',
          nodeKey,
          nodeCategory,
          layerRules as LayerRuleStyle,
          layersStyle,
          getStyle,
          computeStyle,
        )
      : buildSimpleRenderer('polygon', nodeKey, nodeCategory, layersStyle, getStyle);
    return;
  }

  layer.renderer = uniqueRendered
    ? buildUniqueValueRenderer(
        'line',
        nodeKey,
        nodeCategory,
        layerRules as LayerRuleStyle,
        layersStyle,
        getStyle,
        computeStyle,
      )
    : buildSimpleRenderer('line', nodeKey, nodeCategory, layersStyle, getStyle);
}
